<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'BillingListItem',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    oid: {
      type: String,
      required: false,
    },
    templateParams: {
      type: Object,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getContainer = (...args) => {
    return {
      self: $fn.value.urlFor('create_billing_list_item'),
      object: 'list',
      data: [],
    };
  };
  const getPayload = (...args) => {
    const [editor] = args;
    const exclude = [];

    return editor.isNew
      ? editor.getData({ exclude })
      : editor.getChanges({ exclude, skipEmbeddedLists: true });
  };

  const formId = ref(null);
  const formName = ref('billingListItem');
  const messages = ref({
    createTitle: $fn.value.pgettext('BillingListItem', 'New billing list item'),
  });
  const resource = ref(null);
  const editor = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    formName: formName.value,
    messages: messages.value,
    resource: resource.value,
    editor: editor.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_resource = async () => {
    return props.oid
      ? $fn.value.fetch('read_billing_list_item', {
          parameters: { id: props.oid },
          asResource: true,
        })
      : $fn.value.fetch('get_billing_list_item_template', {
          parameters: props.templateParams,
          asResource: true,
        });
  };
  const $$load_editor = async () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'formName' },
      { name: 'messages' },
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      formId: $$load_formId,
      resource: $$load_resource,
      editor: $$load_editor,
    },
    ux: {},
    dataRefs: {
      formId,
      formName,
      messages,
      resource,
      editor,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isDeletable = computed(() => {
    return props.oid != null;
  });
  const editTitle = computed(() => {
    if (loaded.value) {
      return resource.value.description;
    }
    return null;
  });
  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: resource.value.isNew
          ? messages.value.createTitle
          : messages.value.editTitle,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.templateParams,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('templateParams');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_4typ42l4f_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_reloadSlickgrid_khzv7yr30_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'billingListItem',
    };
  };
  const act_1040167445267876_emit_ot0tn2usu_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_1040167445267876_reloadSlickgrid_7o4oz639h_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'billingList',
    };
  };
  const act_1040167445267876_rest_w25gwwx0k_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: getContainer(editor.value),
      resource: editor.value,
      data: getPayload(editor.value),
    };
  };
  const act_5534025912102772_emit_yclv8pjmb_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };

  const act_7315092382398562_closeModal_4typ42l4f =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_4typ42l4f_getActionArgs,
    });
  const inst_evh_7315092382398562_close_3qbh585cf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_4typ42l4f,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_3qbh585cf],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_1lxvpw4gt =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1afa3kbfl =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_1lxvpw4gt,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1afa3kbfl],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_0fipmfjjv =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_nk0qaz660 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_0fipmfjjv,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_nk0qaz660],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_9rqnuyv59 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_s6g54sk5u =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_9rqnuyv59,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_s6g54sk5u],
    event: 'reload',
  });
  const act_6466705138206980_closeComponent_vdh5z9sne =
    new actionTypes.CloseComponentAction({});
  const inst_evh_6466705138206980_deleted_fnvm7p2fl =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeComponent_vdh5z9sne,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_6466705138206980_reloadSlickgrid_khzv7yr30 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6466705138206980_reloadSlickgrid_khzv7yr30_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_32p5qogn7 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadSlickgrid_khzv7yr30,
      event: 'deleted',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_fnvm7p2fl,
      inst_evh_6466705138206980_deleted_32p5qogn7,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_emit_ot0tn2usu = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_ot0tn2usu_getActionArgs,
  });
  const inst_evh_1040167445267876_saved_7xaolnr93 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_ot0tn2usu,
      event: 'saved',
      displayName: 'emit',
    });
  const act_1040167445267876_reloadSlickgrid_7o4oz639h =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_1040167445267876_reloadSlickgrid_7o4oz639h_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_85g72zj0b =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadSlickgrid_7o4oz639h,
      event: 'saved',
      displayName: 'reloadSlickgrid',
    });
  const act_1040167445267876_rest_w25gwwx0k = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_w25gwwx0k_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_saved_7xaolnr93,
      inst_evh_1040167445267876_saved_85g72zj0b,
    ],
  });
  const inst_evh_1040167445267876_submit_8fqgbsqgq =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_w25gwwx0k,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_8fqgbsqgq],
    event: 'submit',
  });
  const act_5534025912102772_reloadComponentData_d6xyudbll =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5534025912102772_reload_ffs5a1vnd =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_reloadComponentData_d6xyudbll,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_5534025912102772_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_reload_ffs5a1vnd],
    event: 'reload',
  });
  const act_5534025912102772_closeComponent_m8lkhp8yi =
    new actionTypes.CloseComponentAction({});
  const inst_evh_5534025912102772_close_wubelgh22 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeComponent_m8lkhp8yi,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_5534025912102772_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_close_wubelgh22],
    event: 'close',
  });
  const act_5534025912102772_emit_yclv8pjmb = new actionTypes.EmitAction({
    actionArgs: act_5534025912102772_emit_yclv8pjmb_getActionArgs,
  });
  const inst_evh_5534025912102772_changed_4zs8gm7ty =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_emit_yclv8pjmb,
      event: 'changed',
      displayName: 'emit',
    });
  const inst_evh_5534025912102772_changed = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_changed_4zs8gm7ty],
    event: 'changed',
  });
  const act_2806493579509315_closeComponent_sjyczm9o2 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2806493579509315_clickPreventStop_r8qgdvpb5 =
    new actionTypes.EventHandlerImpl({
      action: act_2806493579509315_closeComponent_sjyczm9o2,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2806493579509315_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2806493579509315_clickPreventStop_r8qgdvpb5],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_3qbh585cf,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_1afa3kbfl,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_nk0qaz660,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_s6g54sk5u,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_fnvm7p2fl,
    inst_evh_6466705138206980_deleted_32p5qogn7,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_saved_7xaolnr93,
    inst_evh_1040167445267876_saved_85g72zj0b,
    inst_evh_1040167445267876_submit_8fqgbsqgq,
    save: inst_evh_1040167445267876_submit_8fqgbsqgq,
    inst_evh_1040167445267876_submit,
    inst_evh_5534025912102772_reload_ffs5a1vnd,
    inst_evh_5534025912102772_reload,
    inst_evh_5534025912102772_close_wubelgh22,
    inst_evh_5534025912102772_close,
    inst_evh_5534025912102772_changed_4zs8gm7ty,
    inst_evh_5534025912102772_changed,
    inst_evh_2806493579509315_clickPreventStop_r8qgdvpb5,
    inst_evh_2806493579509315_clickPreventStop,
  };

  const evh_7315092382398562_close_3qbh585cf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_3qbh585cf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_1afa3kbfl = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1afa3kbfl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_nk0qaz660 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_nk0qaz660.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_s6g54sk5u = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_s6g54sk5u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_fnvm7p2fl = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_fnvm7p2fl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_32p5qogn7 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_32p5qogn7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_7xaolnr93 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_7xaolnr93.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_85g72zj0b = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_85g72zj0b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_8fqgbsqgq = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_8fqgbsqgq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload_ffs5a1vnd = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload_ffs5a1vnd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_reload = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close_wubelgh22 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close_wubelgh22.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_close = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed_4zs8gm7ty = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed_4zs8gm7ty.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_changed = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2806493579509315_clickPreventStop_r8qgdvpb5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2806493579509315_clickPreventStop_r8qgdvpb5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2806493579509315_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2806493579509315_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="BillingListItem"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.billingList.color"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcBillingListItemHeader
          :item="resource"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcBillingListItemHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        :readonly="resource.isReadOnly"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetBillingListItem
          :item="editor"
          :form="forms[formName]"
          @reload="evh_5534025912102772_reload($event, {})"
          @close="evh_5534025912102772_close($event, {})"
          @changed="evh_5534025912102772_changed($event, {})"
        >
        </FieldsetBillingListItem>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.register.color"
        v-if="loaded"
      >
        <ItpBox :gap="2" class="ms-auto">
          <ItpButton
            :form="forms[formName]"
            :text="pgettext('BillingListItem/Button', 'Create item')"
            type="submit"
            :spinning="actions.save.isRunning"
            v-if="resource.isNew"
          >
          </ItpButton>
          <ItpButton
            :form="formId"
            :text="pgettext('Button', 'Save changes')"
            type="submit"
            :spinning="actions.save.isRunning"
            v-if="!resource.isNew && !resource.isReadOnly && forms[formName]"
          >
          </ItpButton>
          <ItpButton
            :text="gettext('Close')"
            variant="default"
            @click.prevent.stop="
              evh_2806493579509315_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcFooter>
    </template>
  </ItpPage>
</template>
